/**
 * ScheduledForm — shared create/edit modal for a planner ScheduledItem.
 * Used by all planning views (calendar, list, kanban, gantt). Supports an
 * alarm (notification) and recurrence (periodificación, new items only).
 */
import { useState } from 'react';
import toast from 'react-hot-toast';
import { TrashIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common';
import {
  plannerApi,
  type PlannerResource,
  type PlannerService,
  type PlannerActivity,
  type PlannerScheduledItem,
  type PlannerTaxonomies,
} from '@/services/api';
import { inputClass, labelClass, treeById, ymd, hm, mondayIndex, WEEKDAYS, ALARM_OPTIONS } from './plannerShared';

export interface CreateCtx { resourceId: string; start: Date }

export default function ScheduledForm({ tax, activities, services, resources, initial, createCtx, canManage, onClose, onSaved }: {
  tax: PlannerTaxonomies | null;
  activities: PlannerActivity[];
  services: PlannerService[];
  resources: PlannerResource[];
  initial: PlannerScheduledItem | null;
  createCtx: CreateCtx | null;
  canManage: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const baseStart = initial ? new Date(initial.start) : createCtx!.start;
  const baseEnd = initial ? new Date(initial.end) : new Date(createCtx!.start.getTime() + 30 * 60000);
  const initialAlarmMin = (() => {
    if (!initial?.alarmAt) return -1;
    return Math.round((new Date(initial.start).getTime() - new Date(initial.alarmAt).getTime()) / 60000);
  })();

  const [resourceId, setResourceId] = useState(initial?.resourceId || createCtx!.resourceId);
  const [activityId, setActivityId] = useState(initial?.activityId || '');
  const [serviceId, setServiceId] = useState(initial?.serviceId || '');
  const [dateStr, setDateStr] = useState(ymd(baseStart));
  const [startTime, setStartTime] = useState(hm(baseStart));
  const [durationMin, setDurationMin] = useState(Math.max(15, Math.round((baseEnd.getTime() - baseStart.getTime()) / 60000)));
  const [clientLabel, setClientLabel] = useState(initial?.clientLabel || '');
  const [stateNodeId, setStateNodeId] = useState(initial?.stateNodeId || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [alarmMin, setAlarmMin] = useState<number>(initialAlarmMin);
  const [saving, setSaving] = useState(false);

  const [repeat, setRepeat] = useState(false);
  const [weekdays, setWeekdays] = useState<number[]>([mondayIndex(baseStart)]);
  const [fromStr, setFromStr] = useState(ymd(baseStart));
  const [toStr, setToStr] = useState(ymd(new Date(baseStart.getTime() + 28 * 24 * 60 * 60000)));

  const stateNodes = treeById(tax, 'task-states')?.nodes || [];

  const onServiceChange = (id: string) => {
    setServiceId(id);
    const svc = services.find((s) => s._id === id);
    if (svc?.duration) setDurationMin(svc.duration);
  };

  const buildOne = (y: number, m: number, d: number) => {
    const [hh, mm] = startTime.split(':').map(Number);
    const start = new Date(y, m - 1, d, hh, mm, 0, 0);
    const end = new Date(start.getTime() + Math.max(15, durationMin) * 60000);
    const alarmAt = alarmMin >= 0 ? new Date(start.getTime() - alarmMin * 60000) : null;
    return { start, end, alarmAt };
  };

  const commonPayload = () => ({
    resourceId,
    activityId: activityId || null,
    serviceId: serviceId || null,
    clientLabel: clientLabel.trim() || undefined,
    stateNodeId: stateNodeId || null,
    notes: notes.trim() || undefined,
  });

  const toggleWeekday = (i: number) => setWeekdays((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));

  const save = async () => {
    if (!resourceId) { toast.error('Selecciona un recurso'); return; }
    setSaving(true);
    try {
      if (!initial && repeat) {
        if (weekdays.length === 0) { toast.error('Selecciona al menos un día'); setSaving(false); return; }
        const [fy, fm, fd] = fromStr.split('-').map(Number);
        const [ty, tm, td] = toStr.split('-').map(Number);
        const from = new Date(fy, fm - 1, fd);
        const to = new Date(ty, tm - 1, td);
        const itemsToCreate: Partial<PlannerScheduledItem>[] = [];
        for (let d = new Date(from); d <= to && itemsToCreate.length < 366; d.setDate(d.getDate() + 1)) {
          if (!weekdays.includes(mondayIndex(d))) continue;
          const { start, end, alarmAt } = buildOne(d.getFullYear(), d.getMonth() + 1, d.getDate());
          itemsToCreate.push({ ...commonPayload(), start: start.toISOString(), end: end.toISOString(), alarmAt: alarmAt ? alarmAt.toISOString() : null });
        }
        if (itemsToCreate.length === 0) { toast.error('El rango no genera ninguna fecha'); setSaving(false); return; }
        const res = await plannerApi.createScheduledBulk(itemsToCreate);
        toast.success(`${res.data?.data?.created ?? itemsToCreate.length} planificaciones creadas`);
        onSaved();
        return;
      }
      const [y, m, d] = dateStr.split('-').map(Number);
      const { start, end, alarmAt } = buildOne(y, m, d);
      const payload = { ...commonPayload(), start: start.toISOString(), end: end.toISOString(), alarmAt: alarmAt ? alarmAt.toISOString() : null };
      if (initial) await plannerApi.updateScheduled(initial._id, payload);
      else await plannerApi.createScheduled(payload);
      toast.success('Planificación guardada');
      onSaved();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!initial) return;
    setSaving(true);
    try {
      await plannerApi.deleteScheduled(initial._id);
      toast.success('Eliminado');
      onSaved();
    } catch {
      toast.error('Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-900">{initial ? 'Editar planificación' : 'Nueva planificación'}</h3>

        <div>
          <label className={labelClass}>Recurso *</label>
          <select className={inputClass} value={resourceId} onChange={(e) => setResourceId(e.target.value)} disabled={!canManage}>
            {resources.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Actividad</label>
            <select className={inputClass} value={activityId} onChange={(e) => setActivityId(e.target.value)} disabled={!canManage}>
              <option value="">—</option>
              {activities.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Servicio</label>
            <select className={inputClass} value={serviceId} onChange={(e) => onServiceChange(e.target.value)} disabled={!canManage}>
              <option value="">—</option>
              {services.map((s) => <option key={s._id} value={s._id}>{s.name} ({s.duration}m)</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {!repeat && (
            <div>
              <label className={labelClass}>Fecha</label>
              <input type="date" className={inputClass} value={dateStr} onChange={(e) => setDateStr(e.target.value)} disabled={!canManage} />
            </div>
          )}
          <div>
            <label className={labelClass}>Hora inicio</label>
            <input type="time" className={inputClass} value={startTime} onChange={(e) => setStartTime(e.target.value)} disabled={!canManage} />
          </div>
          <div>
            <label className={labelClass}>Duración (min)</label>
            <input type="number" min={15} step={5} className={inputClass} value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))} disabled={!canManage} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Cliente (etiqueta)</label>
            <input className={inputClass} value={clientLabel} onChange={(e) => setClientLabel(e.target.value)} placeholder="Hab. 201 / nombre" disabled={!canManage} />
          </div>
          <div>
            <label className={labelClass}>Estado</label>
            <select className={inputClass} value={stateNodeId} onChange={(e) => setStateNodeId(e.target.value)} disabled={!canManage}>
              <option value="">—</option>
              {stateNodes.map((n) => <option key={n._id} value={n._id}>{n.name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>Aviso (notificación)</label>
          <select className={inputClass} value={alarmMin} onChange={(e) => setAlarmMin(Number(e.target.value))} disabled={!canManage}>
            {ALARM_OPTIONS.map((o) => <option key={o.min} value={o.min}>{o.label}</option>)}
          </select>
        </div>

        {!initial && canManage && (
          <div className="border-t border-gray-100 pt-3 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} /> Repetir (periodificar)
            </label>
            {repeat && (
              <>
                <div className="flex flex-wrap gap-1">
                  {WEEKDAYS.map((w, i) => (
                    <button key={w} type="button" onClick={() => toggleWeekday(i)}
                      className={`px-2.5 py-1 text-xs rounded-md border ${weekdays.includes(i) ? 'bg-vida-600 text-white border-vida-600' : 'border-gray-300 text-gray-600'}`}>
                      {w}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Desde</label>
                    <input type="date" className={inputClass} value={fromStr} onChange={(e) => setFromStr(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Hasta</label>
                    <input type="date" className={inputClass} value={toStr} onChange={(e) => setToStr(e.target.value)} />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div>
          <label className={labelClass}>Notas</label>
          <textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canManage} />
        </div>

        <div className="flex justify-between items-center pt-2">
          <div>
            {initial && canManage && (
              <button onClick={remove} disabled={saving} className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50">
                <TrashIcon className="h-4 w-4" /> Eliminar
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50">Cerrar</button>
            {canManage && (
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50">
                {saving && <LoadingSpinner size="sm" />} Guardar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
